import * as React from 'react';
import { cn } from '@relay-towing/tokens';
import {
  mockPanelRecipe,
  pitchCardEyebrowRecipe,
  pitchCardRecipe,
  pitchCardTextRecipe,
  pitchFooterBrandRecipe,
  pitchFooterBrandTextRecipe,
  pitchHeroLeadRecipe,
  pitchHeroRecipe,
  pitchPanelRecipe,
  pitchSectionRecipe,
  pitchShellRecipe,
  pitchVideoFrameRecipe,
  pullQuoteRecipe,
  type PitchHeroRecipeProps,
  type PitchSectionRecipeProps,
  type PitchShellRecipeProps,
} from '../recipes/pitch.recipe';
import { Text } from './Text';

/*
 * The prospect's sales page, in parts. See pitch.recipe.ts.
 *
 *   <PitchShell>
 *     <PitchSection space="md"><PitchHero title=… description=… /></PitchSection>
 *     <PitchSection space="lg">
 *       <PitchCard preview={<FeatureMock feature="dispatch" />} title=… description=… />
 *     </PitchSection>
 *   </PitchShell>
 */

export interface PitchShellProps
  extends React.HTMLAttributes<HTMLDivElement>,
    PitchShellRecipeProps {}

/**
 * Switches on the sales theme for everything inside it: cream ground, deep
 * green, Schibsted Grotesk, soft corners, the big headline sizes. The font
 * itself is loaded by the page that uses it.
 */
export const PitchShell = React.forwardRef<HTMLDivElement, PitchShellProps>(
  function PitchShell({ fill, className, ...props }, ref) {
    return (
      <div
        ref={ref}
        className={cn(pitchShellRecipe({ fill }), className)}
        {...props}
      />
    );
  }
);

export interface PitchSectionProps
  extends React.HTMLAttributes<HTMLElement>,
    PitchSectionRecipeProps {
  as?: 'section' | 'header' | 'footer' | 'div' | 'main';
}

/** One band of the page, in the centred column with the page gutters. */
export const PitchSection = React.forwardRef<HTMLElement, PitchSectionProps>(
  function PitchSection(
    { as: Tag = 'section', space, width, className, ...props },
    ref
  ) {
    return (
      <Tag
        ref={ref as React.Ref<HTMLDivElement>}
        className={cn(pitchSectionRecipe({ space, width }), className)}
        {...props}
      />
    );
  }
);

export interface PitchHeroProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'>,
    PitchHeroRecipeProps {
  /** A chip above the headline: who the page is for. */
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
}

/**
 * The opening: the headline big on the left, one plain line on the right,
 * on a shared baseline, stacked on a phone. `align="center"` centres both.
 */
export const PitchHero = React.forwardRef<HTMLDivElement, PitchHeroProps>(
  function PitchHero(
    { eyebrow, title, description, align, className, ...props },
    ref
  ) {
    const textAlign = align === 'center' ? 'center' : undefined;
    return (
      <div
        ref={ref}
        className={cn(pitchHeroRecipe({ align }), className)}
        {...props}
      >
        <div className={pitchHeroLeadRecipe({ align })}>
          {eyebrow}
          <Text variant="display" align={textAlign}>
            {title}
          </Text>
        </div>
        {description ? (
          <Text variant="body-lg" tone="muted" align={textAlign}>
            {description}
          </Text>
        ) : null}
      </div>
    );
  }
);

export interface PitchCardProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'title'> {
  /** A small picture of the thing, above the words: a FeatureMock. */
  preview?: React.ReactNode;
  /** A chip above the title: why this card is here, e.g. a Badge. */
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  /**
   * One short, small line under the title, in place of a description: what
   * the prospect said about this. Keeps the card the height of its neighbours.
   */
  note?: React.ReactNode;
}

/** A card on the cream ground. A feature, or something the prospect told us. */
export const PitchCard = React.forwardRef<HTMLElement, PitchCardProps>(
  function PitchCard(
    {
      preview,
      eyebrow,
      title,
      description,
      note,
      className,
      children,
      ...props
    },
    ref
  ) {
    return (
      <article
        ref={ref}
        className={cn(pitchCardRecipe(), className)}
        {...props}
      >
        {preview}
        <div className={pitchCardTextRecipe()}>
          {eyebrow ? (
            <div className={pitchCardEyebrowRecipe()}>{eyebrow}</div>
          ) : null}
          <Text variant="h3">{title}</Text>
          {description ? <Text tone="muted">{description}</Text> : null}
          {note ? (
            <Text variant="body-sm" tone="muted">
              {note}
            </Text>
          ) : null}
        </div>
        {children}
      </article>
    );
  }
);

export type MockPanelProps = React.HTMLAttributes<HTMLDivElement>;

/**
 * The soft inner panel of a feature card: a still, made-up glimpse of the
 * screen the card talks about. Not interactive and never real data.
 */
export const MockPanel = React.forwardRef<HTMLDivElement, MockPanelProps>(
  function MockPanel({ className, ...props }, ref) {
    return (
      <div
        ref={ref}
        aria-hidden="true"
        className={cn(mockPanelRecipe(), className)}
        {...props}
      />
    );
  }
);

export interface PullQuoteProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'children'> {
  /** The words, without quote marks: curly ones are added. */
  quote: string;
  /** Who said it, under the quote: "Danielle, on the call". */
  cite?: React.ReactNode;
}

/** Something the prospect said, quietly set apart from the copy around it. */
export const PullQuote = React.forwardRef<HTMLElement, PullQuoteProps>(
  function PullQuote({ quote, cite, className, ...props }, ref) {
    return (
      <figure ref={ref} className={cn(pullQuoteRecipe(), className)} {...props}>
        <Text as="blockquote" variant="body-lg">
          {`“${quote}”`}
        </Text>
        {cite ? (
          <Text as="figcaption" variant="body-sm" tone="muted">
            {cite}
          </Text>
        ) : null}
      </figure>
    );
  }
);

export type PitchPanelProps = React.HTMLAttributes<HTMLDivElement>;

/** The closing band: deep green with cream type, and the last button. */
export const PitchPanel = React.forwardRef<HTMLDivElement, PitchPanelProps>(
  function PitchPanel({ className, ...props }, ref) {
    return (
      <div ref={ref} className={cn(pitchPanelRecipe(), className)} {...props} />
    );
  }
);

/** The positioned frame around the video. */
export const PitchVideoFrame = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function PitchVideoFrame({ className, children, ...props }, ref) {
  return (
    <div ref={ref} className={cn(pitchVideoFrameRecipe(), className)} {...props}>
      {children}
    </div>
  );
});

/** The small linked Relay name in the page footer. */
export function PitchFooterBrand({ href }: { href: string }) {
  return (
    <a href={href} className={pitchFooterBrandRecipe()}>
      <Text
        as="span"
        variant="body-strong"
        className={pitchFooterBrandTextRecipe()}
      >
        Relay Tow
      </Text>
    </a>
  );
}
